import { useEffect, useState } from "react"
import { Grid } from "@mui/material"
import Loader from "../../../components/Loader"
import MainCard from "../../../components/MainCard"
import ScrollX from "../../../components/ScrollX"
import ReactTable from "../../../components/ReactTable"
import ReportsUploadRequestFilter from "./ReportUploadRequestFilter"
import { useSearchParams } from "react-router-dom"
import useReports from "hooks/useReports"
import reportUploadRequestTableColumns from "./reportUploadRequestTableColumns"
import { ReportUploadColumn, ReportUploadRequest, ReportUploadRequestFilterType, ReportUploadRequestStatus } from "types/reports"
import ReportUploadRequestModal from "./modals/ReportUploadRequestModal"
import axios from "utils/axios"

interface QueryParam {
  [key: string]: string
}

const ReportUploadRequestsPage = () => {
  const {
    fetchReportUploadRequestList,
    paginatedReportUploadRequestList
  } = useReports()

  const [limit, setLimit] = useState(10)
  const [pageIndex, setPageIndex] = useState(0)
  const [filter, setFilter] = useState<ReportUploadRequestFilterType>({statusList: [ReportUploadRequestStatus.REVIEWING, ReportUploadRequestStatus.REQUEST_APPROVED]})
  const [loading, setLoading] = useState(true)
  const [searchParams, setSearchParams] = useSearchParams()
  const [initialLoad, setInitialLoad] = useState(true)
  const [reportUploadColumns, setReportUploadColumns] = useState<ReportUploadColumn[]>([])
  const [groupedReportUploadColumns, setGroupedReportUploadColumns] = useState<{[key:string]: string[]}>({})
  const [reportUploadRequestList, setReportUploadRequestList] = useState<ReportUploadRequest[]>([])
  const [selectedReportUploadRequest, setSelectedReportUploadRequest] = useState<ReportUploadRequest>()

  useEffect(() => {
    setReportUploadRequestList(paginatedReportUploadRequestList.results)
  }, [paginatedReportUploadRequestList])
 
  const updateSearchParams = (pageIndex?: number, pageLimit?: number) => {
    const queryParams: QueryParam = {}
    if (filter.distributorList && filter.distributorList.length > 0) {
      queryParams["distributorList"] = filter.distributorList.join(",")
    }
    if (filter.statusList && filter.statusList.length > 0) {
      queryParams["statusList"] = filter.statusList.join(",")
    }
    if (pageLimit && pageLimit !== 10) {
      queryParams["limit"] = pageLimit.toString()
    }
    if (pageIndex && pageIndex !== 0) {
      queryParams["page"] = pageIndex.toString()
    }
    setSearchParams({ ...queryParams })
  }

  const checkQueryParamsAndFetchReportUploadRequest = () => {
    const paramLimit = searchParams.get("limit")
    const paramPageIndex = searchParams.get("page")

    const distributorList = searchParams.get("distributorList")
    const statusList = searchParams.get("statusList")
    const id = searchParams.get("id")

    let queryLimit = limit
    let queryPageIndex = pageIndex

    if (distributorList) filter.distributorList = distributorList.split(",")
    if (statusList) filter.statusList = statusList?.split(",")
    if (id) filter.id = parseInt(id)

    if (paramLimit) {
      queryLimit = parseInt(paramLimit)
      setLimit(parseInt(paramLimit))
    }
    if (paramPageIndex) {
      queryPageIndex = parseInt(paramPageIndex)
      setPageIndex(parseInt(paramPageIndex))
    }

    fetchReportUploadRequestList(queryLimit, queryPageIndex * queryLimit, filter).then(() => setLoading(false))
  }

  const fetchReportUploadColumns = () => {
    axios(`/reports/royalty/report_upload_columns/`, {
      method: "get"
    }).then((resp) => {
      if (resp.data.results) {
        setReportUploadColumns([...columns, ...resp.data.results])
      }
    })
  }

  const groupByType = (arr: ReportUploadColumn[]) => {
    const map: { [key: string]: string[] } = {}

    arr.forEach((item) => {
      const key: string = item?.type || ""

      if (!map[key]) {
        map[key] = []
      }

      map[key].push(item?.header || "")
    })

    return map
  }


  useEffect(() => {
    fetchReportUploadColumns()
  }, [])

  useEffect(() => {
    const groupedUploadColumns = groupByType(reportUploadColumns)
    setGroupedReportUploadColumns(groupedUploadColumns)
  }, [reportUploadColumns])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setLoading(true)
      if (initialLoad) {
        checkQueryParamsAndFetchReportUploadRequest()
      } else {
        fetchReportUploadRequestList(limit, pageIndex * limit, filter).then(() => setLoading(false))
        updateSearchParams(pageIndex, limit)
      }
      setInitialLoad(false)
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [filter])

  const gotoPage = (pageIndex: number) => {
    setLoading(true)
    let index = pageIndex
    if (pageIndex <= 0) index = 0
    setPageIndex(index)
    updateSearchParams(index, limit)
    fetchReportUploadRequestList(limit, index * limit, filter).then(() => setLoading(false))
  }

  const setPageSize = (limit: number) => {
    setLoading(true)
    setLimit(limit)
    updateSearchParams(pageIndex, limit)
    fetchReportUploadRequestList(limit, pageIndex * limit, filter).then(() => setLoading(false))
  }
  
  const onViewReportUploadRequest = (reportUploadRequest: ReportUploadRequest) => {
      setSelectedReportUploadRequest(reportUploadRequest)
  }

  const columns = reportUploadRequestTableColumns(onViewReportUploadRequest)

  if (loading) return <Loader />

  return (
    <Grid container spacing={2.5} sx={{ pl: 0 }}>
      <Grid item xs={12}>
        <ReportsUploadRequestFilter filter={filter} setFilter={setFilter} />
      </Grid>
      <Grid item xs={12}>
        <MainCard title={"Report Upload Request"} content={false} sx={{ height: "100%" }}>
          <ScrollX>
            <ReactTable
              columns={columns}
              data={reportUploadRequestList}
              pagination
              paginationProps={{
                gotoPage,
                setPageSize,
                pageIndex,
                pageSize: limit,
                initialPageSize: 10,
                totalCount: paginatedReportUploadRequestList.count || 0
              }}
            />
          </ScrollX>
        </MainCard>
      </Grid>
      {selectedReportUploadRequest && (
        <ReportUploadRequestModal
          reportUploadRequestList={reportUploadRequestList}
          setReportUploadRequestList={setReportUploadRequestList}
          reportUploadRequest={selectedReportUploadRequest}
          setReportUploadRequest={setSelectedReportUploadRequest}
          reportUploadColumnsMap={groupedReportUploadColumns}
        />
      )}
    </Grid>
  )
}

export default ReportUploadRequestsPage
